import React, { useEffect } from 'react';
import * as Yup from 'yup';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  UI_OPERATION_STATUS_SUCCESS,
} from '@uncinc/react-drupal-core';
import {
  FormTextField,
  FormikWrapper,
  FormCheckbox,
  Button,
} from '@uncinc/uncinc-react-kitchen-sink';
import type { ImmutableRootState } from '@uncinc/react-drupal-core/types';

import type { UserProfileAddress } from '../../types/user';

import { useDialogDrawerContext } from '../../context/DialogDrawerContext';

import './style.scss';

const FormFields = ({ showActions = false, allowSetDefault = true, onFormCancel = () => { } }) => {
  const { t } = useTranslation();

  return (
    <>
      <div className="row">
        <div className="col-12 col-sm-6">
          <FormTextField
            title={t('address.first_name')}
            fieldName="given_name"
          />
        </div>
        <div className="col-12 col-sm-6">
          <FormTextField
            title={t('address.family_name')}
            fieldName="family_name"
          />
        </div>
      </div>
      <div className="row">
        <div className="col-12">
          <FormTextField
            title={t('address.postal_code')}
            fieldName="postal_code"
          />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <FormTextField
            title={t('address.address_line1')}
            fieldName="address_line1"
          />
        </div>
        <div className="col-12 col-md-4">
          <FormTextField
            title={t('address.address_line2')}
            fieldName="address_line2"
          />
        </div>
      </div>
      <div className="row">
        <div className="col-12">
          <FormTextField
            title={t('address.locality')}
            fieldName="locality"
          />
        </div>
      </div>
      <div className="row">
        <div className="col-12">
          <FormTextField
            title={t('address.organization')}
            fieldName="organization"
          />
        </div>
      </div>
      {allowSetDefault && (
        <div className="row">
          <div className="col-12">
            <div className="WebformCheckboxes">
              <FormCheckbox
                fieldName="set_default"
                title={t('address.default_address')}
              />
            </div>
          </div>
        </div>
      )}
      {showActions && (
        <div className="row">
          <div className="col-6">
            <Button
              className="Button--block"
              type="submit"
            >
              {t('general.save')}
            </Button>
          </div>
          <div className="col-6">
            <Button
              onClick={onFormCancel}
              className="Button Button--block Button--inverted"
            >
              {t('general.cancel')}
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export interface AddressFormProps {
  address?: UserProfileAddress;
  isDefault?: boolean,
  hideTitle?: boolean,
  showActions?: boolean,
  fieldsOnly?: boolean,
  allowSetDefault?: boolean,
  onSubmit?: Function,
  onCancel?: Function
}

const AddressForm: React.FC<AddressFormProps> = ({
  address,
  isDefault = false,
  hideTitle = false,
  fieldsOnly = false,
  allowSetDefault = true,
  showActions = true,
  onSubmit = () => { },
  onCancel = () => { },
}) => {
  const { t } = useTranslation();
  const {
    closeDialog,
  } = useDialogDrawerContext();
  const profileUpdateStatus = useSelector<ImmutableRootState<string>, string>((state) => state.getIn(['ui', 'profile', 'operations', 'update', 'status'], ''));
  let initialValues = { country_code: 'NL', set_default: isDefault };
  // If an addresss was passed, add the data to the initialValues.
  if (address) {
    initialValues = {
      ...initialValues,
      ...address,
    };
  }

  // Set up the validation.
  const AddressValidationSchema = Yup.object().shape({
    country_code: Yup.string(),
    set_default: Yup.string(),
    given_name: Yup.string().required(t('validation_messages.signup.given_name')),
    family_name: Yup.string().required(t('validation_messages.signup.family_name')),
    organization: Yup.string().when('business_option', {
      is: (val: any) => val === 'business',
      then: Yup.string().required(t('validation_messages.signup.billing_organisation')).max(60),
      otherwise: Yup.string().notRequired(),
    }),
    locality: Yup.string().required(t('validation_messages.signup.billing_locality')),
    postal_code: Yup.string().required(t('validation_messages.signup.billing_postal_code')).matches(/^[1-9][0-9]{3} ?(?!sa|sd|ss)[a-zA-Z ._-]{2}$/, t('validation_messages.signup.billing_postal_code_invalid')),
    address_line1: Yup.string().required(t('validation_messages.signup.billing_address_line1')),
    address_line2: Yup.string().trim().required(t('validation_messages.signup.billing_address_line2')).matches(/^(?:[0-9]{1})/, t('validation_messages.signup.housenumber.matches')),
  });

  // On form submit.
  const onFormSubmit = (data: any) => {
    onSubmit({
      ...data,
      postal_code: data.postal_code.replace(' ', ''),
    });
  };

  // On form cancel.
  const onFormCancel = () => {
    onCancel();
    closeDialog();
  };

  // Listens to the update status and shows a success or error toast message.
  useEffect(() => {
    if (profileUpdateStatus !== null) {
      if (profileUpdateStatus === UI_OPERATION_STATUS_SUCCESS) {
        closeDialog();
      }
    }
  }, [profileUpdateStatus, closeDialog, t]);

  return (
    <div className="AddressForm">
      {!hideTitle && (
        <h2>{t('address.delivery_address')}</h2>
      )}
      {fieldsOnly ? (
        <FormFields
          showActions={showActions}
          onFormCancel={onFormCancel}
          allowSetDefault={allowSetDefault}
        />
      ) : (
        <FormikWrapper
          onSubmit={onFormSubmit}
          initialValues={initialValues}
          validationSchema={AddressValidationSchema}
        >
          <FormFields
            showActions={showActions}
            onFormCancel={onFormCancel}
            allowSetDefault={allowSetDefault}
          />
        </FormikWrapper>
      )}
    </div>
  );
};

export default AddressForm;
